<template>
  <div class="p-1 fm-view">
    <div class="fm-view__head">
      <h4 class="fm-view__title mb-0"><i class="fa-solid fa-list mr-50"></i>{{ title }}</h4>
      <b-button variant="primary" class="btn-icon" v-b-tooltip v-bind:title="$t('columns.self')"
                @click="openColumnsModal">
        <i class="fa-solid fa-bars"></i>
      </b-button>
    </div>

    <!-- Date range (seeded from the KPI, editable) -->
    <calendar-bar :value="dateRange" sync-with-value @input="onDateRange"/>

    <!-- The exact filters behind the KPI — visible and adjustable -->
    <filter-card v-model="filter" :fields="filterFields" search file-export @export="createExcelFile"
                 v-bind:hidden-fields="{ registrationTime: true }"/>

    <!-- Totals pill (count + summed amount for THIS filtered set) -->
    <b-card no-body class="mb-1">
      <b-card-body class="fm-totals">
        <div class="sum-chip" :class="isWithdrawals ? 'sum-chip--withdraw' : 'sum-chip--deposit'">
          <span class="sum-chip__icon">
            <i :class="isWithdrawals ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'"></i>
          </span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ title }} · {{ totalCount }}</span>
            <span class="sum-chip__value">{{ convertToEURO(sum) }}</span>
          </span>
        </div>
      </b-card-body>
    </b-card>

    <!-- One table, the KPI's exact filters → the numbers match the card -->
    <b-card no-body class="mb-1">
      <b-card-body>
        <monetaries-table :filters="filter" :columns="columns" :trans-type="transType"
                          :has-selection="false" :lazy="false"
                          @total="v => totalCount = v" @sum="v => sum = v"/>
      </b-card-body>
    </b-card>

    <columns-modal ref="columns-modal" column-key="filteredMonetaries" v-model="columns"
                   v-bind:columns-names="columnsNames"
                   v-bind:columns-default="columnsDefault"
    />
  </div>
</template>

<script>
// Full-page, URL-driven transactions list opened in a NEW tab from a dashboard
// KPI card. It renders ONE MonetariesTable with the EXACT filter object the KPI
// used (so the count/sum match the card), and layers on the totals pill + an
// editable filter bar so the user can see what was applied and adjust it.
import { mapActions } from 'vuex';
import FilterCard from '@/components/widget/FilterCard';
import CalendarBar from '@/components/widget/CalendarBar';
import ColumnsModal from '@/components/modal/columnsModal';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetariesFilterFields from '@/model/filter/MonetaryFilterFields';
import MonetariesSearchRequest from '@/model/monetary/MonetarySearchRequest';
import Monetary from '@/model/monetary/Monetary';
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'FilteredMonetariesView',
  components: { FilterCard, CalendarBar, ColumnsModal, MonetariesTable },
  mixins: [mixinBase],
  data: () => ({
    filter: MonetariesSearchRequest(),
    filterFields: null,
    title: '',
    dateRange: null,
    totalCount: 0,
    sum: 0,
    // Columns are user-configurable (columns modal) like every other table;
    // the default set matches what this page always showed.
    columns: [],
    columnsNames: [
      ...Object.keys(Monetary()),
      'clientDetails.accountId', 'clientDetails.clientId', 'clientDetails.email', 'clientDetails.country', 'clientDetails.affiliate',
      'clientDetails.clientUsers.representativeId',
      'paymentDetails.processorName', 'paymentDetails.comments', 'clientComment', 'actions',
    ],
    columnsDefault: [
      'clientDetails', 'clientDetails.accountId', 'amount', 'currency', 'type', 'action',
      'depositorUserId', 'statusUserId', 'isFtd', 'registrationTime', 'status',
    ],
  }),
  computed: {
    isWithdrawals() {
      return this.filter && this.filter.type === 'WITHDRAWAL';
    },
    transType() {
      return this.isWithdrawals ? 'withdrawals' : 'deposits';
    },
  },
  created() {
    const q = this.$route.query || {};
    this.title = q.title || this.$t('menu.monetary');
    if (q.f) {
      try {
        // Merge onto the full search-request shape so the FilterCard has every
        // key it expects, then override with the KPI's exact filters.
        this.filter = { ...MonetariesSearchRequest(), ...JSON.parse(q.f) };
      } catch (e) {
        this.filter = MonetariesSearchRequest();
      }
    }
    // Seed the CalendarBar display from the incoming registrationTime range.
    const rt = this.filter && this.filter.registrationTime;
    if (rt && rt.start && rt.end) {
      this.dateRange = {
        from: this.$moment(rt.start).format('YYYY-MM-DD'),
        until: this.$moment(rt.end).format('YYYY-MM-DD'),
      };
    }
    this.filterFields = MonetariesFilterFields();
  },
  methods: {
    ...mapActions('monetary', ['exportMonetaryDataExcel']),
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    // Same export as the main monetary table, over THIS page's exact filters.
    createExcelFile() {
      this.exportMonetaryDataExcel({ filter: { ...this.filter } });
    },
    // CalendarBar → registrationTime filter (same conversion as MonetaryView).
    onDateRange(range) {
      if (!range) return;
      this.$set(this.filter, 'registrationTime', {
        start: range.from ? this.$moment(range.from).startOf('day').toServerDateTimeFormat() : null,
        end: range.until ? this.$moment(range.until).endOf('day').toServerDateTimeFormat() : null,
      });
    },
  },
};
</script>

<style scoped>
.fm-view__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.fm-view__title {
  font-weight: 700;
}
.fm-totals {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.85rem;
  padding: 0.85rem 1.25rem;
}
.sum-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.3rem 0.9rem 0.3rem 0.4rem;
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 6px 16px -8px rgba(20, 24, 50, 0.45);
}
.sum-chip--deposit { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
.sum-chip--withdraw { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); }
.sum-chip__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.85rem;
  flex-shrink: 0;
}
.sum-chip__body { display: flex; flex-direction: column; line-height: 1.15; }
.sum-chip__label { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.9; }
.sum-chip__value { font-size: 1rem; font-weight: 800; font-variant-numeric: tabular-nums; }
</style>
